import { createApi } from "@reduxjs/toolkit/query/react";
import { components, operations } from "../../types/api";
import { baseQueryWithReauth } from "../../utils/baseQuery";

type ApiResponseCampaignArray =
  components["schemas"]["ApiResponse_CampaignType-Array_"];
type ApiResponseCampaign =
  components["schemas"]["ApiResponse_CampaignWithRelations_"];
type ApiResponseUserCampaigns =
  components["schemas"]["ApiResponse_UserCampaignsResponse_"];
type ApiResponseCampaignDbArray =
  components["schemas"]["ApiResponse_CampaignWithRelations-Array_"];
type ApiResponseCampaignMetrics =
  components["schemas"]["ApiResponse__metrics-InternalAggregatedMetrics-Array__"];
type Platform = components["schemas"]["_36_Enums.Platform"];
type CampaignFormatsResponse =
  components["schemas"]["ApiResponse_CampaignFormatsResponse_"];

// Define the campaign create request type based on the API schema
type CreateCampaignRequest =
  operations["CreateCampaign"]["requestBody"]["content"]["application/json"];

// Define the campaign update request type based on the API schema
type UpdateCampaignRequest =
  operations["UpdateCampaign"]["requestBody"]["content"]["application/json"];

export const campaignApi = createApi({
  reducerPath: "campaignApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Campaigns",
    "UserCampaigns",
    "CreativeMetrics",
    "PublisherMetrics",
    "InternalCampaignMetrics",
  ],
  endpoints: (builder) => ({
    // Get all campaigns
    getCampaigns: builder.query<ApiResponseCampaignArray, void>({
      query: () => "/campaigns",
      providesTags: ["Campaigns"],
    }),

    // Get campaigns for a specific partner
    getCampaignsForPartner: builder.query<ApiResponseCampaignArray, string>({
      query: (partnerId) => `/campaigns/partner/${partnerId}`,
      providesTags: (result) => {
        const campaigns = result?.data as Array<{ id?: string }> | undefined;
        return campaigns && campaigns.length > 0
          ? [
              ...campaigns.map((campaign) => ({
                type: "Campaigns" as const,
                id: campaign.id || "",
              })),
              { type: "UserCampaigns" as const, id: "LIST" },
            ]
          : [{ type: "UserCampaigns" as const, id: "LIST" }];
      },
    }),

    // Get a specific campaign by ID
    getCampaignById: builder.query<ApiResponseCampaign, { campaignId: string }>(
      {
        query: ({ campaignId }) => `/campaigns/${campaignId}`,
        providesTags: (result, error, { campaignId }) => [
          { type: "Campaigns", id: campaignId },
        ],
      }
    ),

    // Create a new campaign
    createCampaign: builder.mutation<
      ApiResponseCampaign,
      CreateCampaignRequest
    >({
      query: (newCampaign) => ({
        url: "/campaigns",
        method: "POST",
        body: newCampaign,
      }),
      invalidatesTags: [{ type: "UserCampaigns", id: "LIST" }],
    }),

    // Update an existing campaign
    updateCampaign: builder.mutation<
      ApiResponseCampaign,
      { campaignId: string; updates: UpdateCampaignRequest }
    >({
      query: ({ campaignId, updates }) => ({
        url: `/campaigns/${campaignId}`,
        method: "PUT",
        body: updates,
      }),
      invalidatesTags: (result, error, { campaignId }) => [
        { type: "Campaigns", id: campaignId },
        "CreativeMetrics",
        "PublisherMetrics",
        "InternalCampaignMetrics",
      ],
    }),

    // Delete a campaign
    deleteCampaign: builder.mutation<ApiResponseCampaign, string>({
      query: (campaignId) => ({
        url: `/campaigns/${campaignId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Campaigns", id },
        { type: "UserCampaigns", id: "LIST" },
      ],
    }),

    // Get campaigns for the current user with optional date range
    getUserCampaigns: builder.query<
      ApiResponseUserCampaigns,
      { platform: Platform; companyId: string }
    >({
      query: (params) => {
        // Both platform and companyId are mandatory
        return `/campaigns/user-campaigns?platform=${params.platform}&companyId=${params.companyId}`;
      },
      providesTags: (result) => {
        const campaigns = result?.data?.campaigns as
          | Array<{ id?: string }>
          | undefined;
        return campaigns && campaigns.length > 0
          ? [
              ...campaigns.map((campaign) => ({
                type: "Campaigns" as const,
                id: campaign.id || "",
              })),
              { type: "UserCampaigns" as const, id: "LIST" },
            ]
          : [{ type: "UserCampaigns" as const, id: "LIST" }];
      },
      // Force refetch when parameters change to ensure loading state is triggered
      forceRefetch: ({ currentArg, previousArg }) => {
        return (
          currentArg?.platform !== previousArg?.platform ||
          currentArg?.companyId !== previousArg?.companyId
        );
      },
    }),

    // Get database campaigns for the current user
    getUserDbCampaigns: builder.query<ApiResponseCampaignDbArray, void>({
      query: () => "/campaigns/user-db-campaigns",
      providesTags: (result) => {
        const campaigns = result?.data as Array<{ id?: string }> | undefined;
        return campaigns && campaigns.length > 0
          ? [
              ...campaigns.map((campaign) => ({
                type: "Campaigns" as const,
                id: campaign.id || "",
              })),
              { type: "UserCampaigns" as const, id: "LIST" },
            ]
          : [{ type: "UserCampaigns" as const, id: "LIST" }];
      },
    }),

    // Get campaign metrics for a specific campaign
    getCampaignMetrics: builder.query<
      ApiResponseCampaignMetrics,
      { campaignId: string; startDate?: string; endDate?: string }
    >({
      query: ({ campaignId, startDate, endDate }) => {
        let queryString = `/campaigns/metrics/${campaignId}`;
        const params = new URLSearchParams();

        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);

        const queryParams = params.toString();
        if (queryParams) queryString += `?${queryParams}`;

        return queryString;
      },
      providesTags: ["Campaigns"],
    }),

    // Get campaign formats
    getCampaignFormats: builder.query<CampaignFormatsResponse, void>({
      query: () => "/campaigns/formats",
      providesTags: ["Campaigns"],
    }),
  }),
});

export const {
  useGetCampaignsQuery,
  useGetCampaignsForPartnerQuery,
  useGetCampaignByIdQuery,
  useGetUserCampaignsQuery,
  useGetUserDbCampaignsQuery,
  useGetCampaignMetricsQuery,
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useDeleteCampaignMutation,
  useGetCampaignFormatsQuery,
} = campaignApi;
